import { useCallback, useEffect, useMemo, useState } from 'react';
import { erApiFeil } from '../api/feil.ts';
import { useMe } from '../api/useMe.ts';
import { useInnstillinger, useOppdaterInnstillinger, type TenantSettings } from '../api/useSettings.ts';
import { Kartknapp } from '../innboks/Kartknapp.tsx';
import type { Tast } from '../innboks/Tastaturkart.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Knapp } from '../innboks/Detalj.tsx';
import { Bryter } from '../kjentefeil/Bryter.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Fot } from '../innstillinger/Fot.tsx';
import { Kopier } from '../innstillinger/Kopier.tsx';
import { ApiNoekler } from '../innstillinger/Noekler.tsx';
import { Rad, RadProvider, Seksjon, TekniskeBryter, useRadstyring } from '../innstillinger/Rad.tsx';
import { useUtkast, type Utkast } from '../innstillinger/utkast.tsx';
import { Webhooks } from '../innstillinger/Webhooks.tsx';
import { Om } from '../skall/Om.tsx';
import { useKonfig } from '../konfigKontekst.tsx';
import { useTilgang } from '../skall/tilgang.ts';
import { BareEier } from './BareEier.tsx';
import { PanelbrukereRad } from './panelbrukere/PanelbrukereRad.tsx';
import { Ikon } from '../status/Ikon.tsx';

/** Kontraktens grenser for det Organisasjon skriver (TenantSettingsUpdate). */
const RETENSJON = [30, 730] as const;
const SKJERMBILDE_DAGER = 90;

/**
 * Organisasjon (DD-01, R5.3): det som gjelder LEIETAKEREN og ikke arbeidsrommet — navnet,
 * personvernerklæringen, retensjonen, brukergruppen, innsendernes innlogging, panelbrukere,
 * API-nøkler og varslene ut. Det finnes én gang, ikke én gang per rom, og derfor ligger
 * siden utenfor rommene og bare for eier.
 *
 * Samme rader, samme fot og samme utkastmodell som Innstillinger — men et EGET utkast:
 * de to sidene lagrer hver for seg mot samme `PATCH /v1/admin/settings`, der utelatt felt
 * betyr «ikke rør» (P3).
 *
 * `GET /settings` er alle roller, så siden skjules i UI og ikke i API-et (DD-01). Kommer en
 * behandler eller leser hit på direkte lenke, sier siden bare hvem den er for og hvem du er,
 * og henter ingenting: panelbrukerlista er eierens alene (DD-68).
 */
export const FELT_I_SEKSJON: Readonly<Record<string, readonly string[]>> = {
  leietakeren: ['name', 'privacyUrl', 'retentionDays', 'minorsPresent'],
  innlogging: ['portalDomain', 'loginRedirectUrl', 'jwksUrl', 'segmentKeys'],
  // K8, 2026-09-21: tofaktor for panelbrukerne er leietakerens valg, og eierens alene.
  // H2.7: domenesetet er også eierens valg om hvem som kommer inn, og står rett etter tofaktor.
  tilgang: ['adminMfaRequired', 'seatDomains'],
};

/**
 * Ordene på raden, ett sted: verdilinjen, dialogen og fotens «hva lagres» bruker de samme.
 *
 * «Tofaktor» er et bevisst unntak fra regelen om at «faktor» aldri står på en skjerm
 * (DD-60, presisert 2026-09-21): databehandleravtalen, Bilag B og personvernerklæringen
 * for adminbrukere bruker ordet til NØYAKTIG denne leseren om nøyaktig denne bryteren, og
 * to ord for én ting er verre enn ett fremmedord. Verdilinjene bruker panelets egne ord.
 */
const BEKREFTELSE = {
  navn: 'Tofaktor ved innlogging',
  paa: 'Alle bekrefter med koden fra appen eller med enheten',
  av: 'E-postkoden alene gir tilgang til alle sakene',
} as const;

/** Feltene hver RAD eier, i sidens rekkefølge — radens merke og fotens «hva lagres». */
const FELT_I_RAD: ReadonlyArray<{ rad: string; navn: string; felter: ReadonlyArray<keyof Utkast> }> = [
  { rad: 'navn', navn: 'Navn', felter: ['name'] },
  { rad: 'personvern', navn: 'Personvernerklæring', felter: ['privacyUrl'] },
  { rad: 'retensjon', navn: 'Retensjon', felter: ['retentionDays'] },
  { rad: 'minorsPresent', navn: 'Mindreårige i brukergruppen', felter: ['minorsPresent'] },
  { rad: 'portal', navn: 'Portal og innlogging', felter: ['portalDomain', 'loginRedirectUrl'] },
  { rad: 'signeringsnokler', navn: 'Signeringsnøkler', felter: ['jwksUrl'] },
  { rad: 'segmentnokler', navn: 'Segmentnøkler', felter: ['segmentKeys'] },
  { rad: 'adminMfaRequired', navn: BEKREFTELSE.navn, felter: ['adminMfaRequired'] },
  { rad: 'domeneseter', navn: 'Domeneseter', felter: ['seatDomains'] },
];

const TASTER: Tast[] = [['Ctrl+S', 'lagre endringer']];

/** URL-en uten protokoll — den vises HELT, aldri kuttet med ellipse. */
const kort = (url: string | undefined | null) => (url ? url.replace(/^https?:\/\//, '') : null);

export function OrganisasjonSide() {
  const tilgang = useTilgang();
  if (!tilgang.kjent) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">Organisasjon</h1>
        </header>
        <div role="status" aria-busy="true" aria-label="Laster organisasjonen" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
        </div>
      </>
    );
  }
  if (!tilgang.eier) return <BareEier />;
  return <Eiersiden />;
}

function Eiersiden() {
  const innstillinger = useInnstillinger();
  // M5.7: leietaker-ID er `iss` i feedback-tokenet og kommer fra «meg» (samme query som
  // skallet); API-adressen er panelets egen byggkonfig. Ingen av dem er hemmelige.
  const tenantId = useMe().data?.tenantId;
  const { apiUrl } = useKonfig();
  const tilgang = useTilgang();
  const oppdater = useOppdaterInnstillinger();
  const { utkast, sett, tøm, antall: endringer } = useUtkast('organisasjon');
  const [toast, setToast] = useState<ToastInnhold | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  /**
   * Å svekke innloggingen til saker som er personopplysninger skjer ikke på ett klikk i en
   * bryter. Dialogen er der konsekvensen står i klartekst; å slå kravet PÅ igjen er ingen
   * svekkelse og har ingen dialog.
   */
  const [bekreftAv, setBekreftAv] = useState(false);
  const side = useRadstyring(true);

  const s = innstillinger.data;
  const verdi = useCallback(<K extends keyof TenantSettings & keyof Utkast>(felt: K): TenantSettings[K] => (utkast[felt] !== undefined ? (utkast[felt] as TenantSettings[K]) : (s![felt] as TenantSettings[K])), [utkast, s]);
  const endret = useCallback((rad: string) => (FELT_I_RAD.find((r) => r.rad === rad)?.felter ?? []).some((f) => utkast[f] !== undefined), [utkast]);
  const endretNavn = useMemo(() => FELT_I_RAD.filter((r) => r.felter.some((f) => utkast[f] !== undefined)).map((r) => r.navn), [utkast]);

  const lagre = useCallback(() => {
    if (oppdater.isPending || endringer === 0) return;
    setFeil(null);
    oppdater
      .mutateAsync(utkast)
      .then(() => {
        tøm();
        setToast({ tekst: `Organisasjonen er lagret — ${endringer} ${endringer === 1 ? 'felt' : 'felter'} endret.` });
      })
      .catch((e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Kunne ikke lagre. Prøv igjen.'));
  }, [oppdater, utkast, endringer, tøm]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        lagre();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lagre]);

  if (innstillinger.isPending) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">Organisasjon</h1>
        </header>
        <div role="status" aria-busy="true" aria-label="Laster organisasjonen" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
          <span className="panel-skjelett-linje panel-skjelett-kort" />
        </div>
      </>
    );
  }

  if (innstillinger.isError || !s) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">Organisasjon</h1>
        </header>
        <div role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> Kunne ikke laste innstillingene. {innstillinger.error && erApiFeil(innstillinger.error) ? innstillinger.error.brukertekst : ''}
          <button type="button" className="panel-knapp-liten" onClick={() => void innstillinger.refetch()}>
            Prøv igjen
          </button>
        </div>
      </>
    );
  }

  const minors = verdi('minorsPresent');
  const mfa = verdi('adminMfaRequired');
  const segmentnokler = utkast.segmentKeys ?? s.segmentKeys;
  const domeneseter = utkast.seatDomains ?? s.seatDomains;
  // Raden finnes bare med rommet Hypoteser (DD-78): uten rommet finnes det ingenting å gi sete til.
  const harHypoteser = tilgang.kanSe('hypoteser');
  const personvern = kort(verdi('privacyUrl'));
  const portal = verdi('portalDomain');
  const login = kort(verdi('loginRedirectUrl'));
  const jwks = verdi('jwksUrl');

  return (
    <>
      <header className="panel-topp">
        <h1 className="panel-h1">Organisasjon</h1>
        <Kartknapp tittel="Organisasjon" taster={TASTER} />
      </header>

      <div className="panel-innst">
        <div className="panel-innst-hode">
          <p className="panel-innst-lead">Det som gjelder {s.name} som leietaker, uansett rom.</p>
          <TekniskeBryter paa={side.tekniske} onChange={side.setTekniske} />
        </div>

        <RadProvider value={side}>
          <Seksjon id="leietakeren" tittel="Leietakeren">
            <Rad
              id="navn"
              navn="Navn"
              kode="name"
              handling="Endre"
              endret={endret('navn')}
              verdi={
                <>
                  <strong>{verdi('name')}</strong> · i svarene innsenderne får
                </>
              }
              verdiSmal={verdi('name')}
            >
              <label className="panel-felt">
                <span>Navnet deres</span>
                <input type="text" value={verdi('name')} maxLength={80} onChange={(e) => sett('name', e.target.value)} data-testid="navn" />
              </label>
            </Rad>

            <Rad
              id="personvern"
              navn="Personvernerklæring"
              kode="privacyUrl"
              handling="Endre"
              endret={endret('personvern')}
              verdi={personvern ? <><strong>{personvern}</strong> · i widgeten og portalen</> : 'Ikke satt'}
              verdiSmal={personvern ?? 'Ikke satt'}
            >
              <label className="panel-felt panel-innst-bred">
                <span>Personvernerklæring (https)</span>
                <input type="url" value={verdi('privacyUrl') ?? ''} placeholder="https://leietaker.no/personvern" onChange={(e) => sett('privacyUrl', e.target.value)} data-testid="privacy-url" />
              </label>
            </Rad>

            <Rad
              id="retensjon"
              navn="Retensjon"
              kode="retentionDays"
              handling="Endre"
              endret={endret('retensjon')}
              verdi={
                <>
                  <strong>{verdi('retentionDays')} dager</strong> for lukkede saker · skjermbilder alltid {SKJERMBILDE_DAGER}
                </>
              }
              verdiSmal={`${verdi('retentionDays')} dager`}
            >
              <label className="panel-felt">
                <span>Retensjon for lukkede saker (dager)</span>
                <input type="number" min={RETENSJON[0]} max={RETENSJON[1]} value={verdi('retentionDays')} onChange={(e) => sett('retentionDays', Number(e.target.value))} data-testid="retensjon" />
              </label>
            </Rad>

            {/* Bryteren ALENE i verdikolonnen: den ER handlingen, og en «Endre» ved siden av
                ville vært et andre sted å gjøre det samme (DD-08-presiseringen). */}
            <Rad
              id="minorsPresent"
              navn="Mindreårige i brukergruppen"
              kode="minorsPresent"
              handling={null}
              endret={endret('minorsPresent')}
              verdi={<Bryter label="Mindreårige i brukergruppen" skjultEtikett paa={minors} onChange={(v) => sett('minorsPresent', v)} testid="minors" />}
            />
            <div className="panel-innst-om">
              <Om tittel="brukergruppen" testid="om-brukergruppen">
                <p>Er brukergruppen mindreårig, strammer plattformen selv: tekstgrensen står på 140 tegn, skjermbilde er av, programmene er forpliktelsesfrie, og community er av. Låsene står på Innstillinger, der de virker.</p>
                <p>Det er dere som vet hvem appen er for. Plattformen spør aldri innsenderen om alder.</p>
              </Om>
            </div>
          </Seksjon>

          <Seksjon id="innlogging" tittel="Innsendernes innlogging">
            <Rad
              id="portal"
              navn="Portal og innlogging"
              kode="portalDomain · loginRedirectUrl"
              handling="Endre"
              endret={endret('portal')}
              verdi={portal ? <><strong>{portal}</strong>{login ? ` · sendes til ${login}, som gir tokenet innsenderen kommer tilbake med` : ' · innloggingsrute ikke satt'}</> : 'Ikke satt'}
              verdiSmal={portal ?? 'Ikke satt'}
            >
              <label className="panel-felt panel-innst-bred">
                <span>Portalens subdomene</span>
                <input type="text" value={portal ?? ''} maxLength={253} placeholder="innspill.leietaker.no" onChange={(e) => sett('portalDomain', e.target.value)} data-testid="portal-domene" />
              </label>
              <label className="panel-felt panel-innst-bred">
                <span>Innloggingsrute (https)</span>
                <input type="url" value={verdi('loginRedirectUrl') ?? ''} placeholder="https://app.leietaker.no/innspel/login" onChange={(e) => sett('loginRedirectUrl', e.target.value)} data-testid="login-url" />
              </label>
              <p className="panel-tekst-liten">Innsenderen sendes til deres egen innloggingsside, og kommer tilbake med et kortlevd token. Panelet ser aldri hvem hun er.</p>
            </Rad>

            {/* M5.7: ID-en og adressen står SAMMEN med signeringsnøklene — det er de tre tingene
                utviklerne deres trenger for token-endepunktet. Bare lesing; «Kopier» er handlingen
                og står i verdikolonnen, som bryteren for mindreårige (avvik 7). */}
            <Rad
              id="leietaker-id"
              navn="Leietaker-ID og API-adresse"
              kode="tenantId"
              handling={null}
              verdi={
                <span className="panel-innst-id-er">
                  <span className="panel-innst-id">
                    <code data-testid="leietaker-id">{tenantId}</code>
                    {tenantId ? <Kopier hva="leietaker-ID" verdi={tenantId} testid="kopier-leietaker-id" /> : null}
                  </span>
                  <span className="panel-innst-id">
                    <code data-testid="api-adresse">{apiUrl}</code>
                    <Kopier hva="API-adressen" verdi={apiUrl} testid="kopier-api-adresse" />
                  </span>
                  <span className="panel-tekst-liten">
                    Leietaker-ID er <code>iss</code> i feedback-tokenet utviklerne deres utsteder.
                  </span>
                </span>
              }
            />

            <Rad
              id="signeringsnokler"
              navn="Signeringsnøkler"
              kode="jwksUrl"
              handling="Endre"
              endret={endret('signeringsnokler')}
              // `jwksUrl` og `publicKeys` er ALTERNATIVER i kontrakten: raden viser den ene som er i bruk.
              verdi={jwks ? <><strong>JWKS</strong> · {kort(jwks)}</> : s.publicKeys.length ? <><strong>{s.publicKeys.length} opplastede nøkler</strong> · {s.publicKeys.map((k) => k.kid).join(', ')}</> : 'Ikke satt'}
              verdiSmal={jwks ? 'JWKS' : s.publicKeys.length ? `${s.publicKeys.length} opplastede` : 'Ikke satt'}
            >
              <label className="panel-felt panel-innst-bred">
                <span>Nøkkelendepunkt (https)</span>
                <input type="url" value={jwks ?? ''} placeholder="https://app.leietaker.no/.well-known/jwks.json" onChange={(e) => sett('jwksUrl', e.target.value)} data-testid="jwks-url" />
              </label>
              <p className="panel-tekst-liten">
                Alternativ til opplastede nøkler. {s.publicKeys.length ? `${s.publicKeys.length} opplastet nøkkel${s.publicKeys.length === 1 ? '' : 'er'} (${s.publicKeys.map((k) => k.kid).join(', ')}) — opplastede nøkler byttes av utviklerne deres over API-et.` : 'Ingen opplastede nøkler.'}
              </p>
            </Rad>

            <Rad
              id="segmentnokler"
              navn="Segmentnøkler"
              kode="segmentKeys"
              handling="Endre"
              endret={endret('segmentnokler')}
              verdi={segmentnokler.length ? <><strong>{segmentnokler.length}</strong> · {segmentnokler.join(', ')} · brukes av betaprogrammet</> : 'Ingen'}
              verdiSmal={segmentnokler.length ? `${segmentnokler.length} nøkler` : 'Ingen'}
            >
              <label className="panel-felt panel-innst-bred">
                <span>Segmentnøkler (kommaseparert)</span>
                <input type="text" value={segmentnokler.join(', ')} placeholder="plan, region" onChange={(e) => sett('segmentKeys', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} data-testid="segmentnokler" />
              </label>
              <p className="panel-tekst-liten">Segmentfeltene panelet får lese fra innloggingstokenet. Andre felter forkastes.</p>
            </Rad>
          </Seksjon>

          <Seksjon id="tilgang" tittel="Tilgang">
            {/* Bryteren ALENE i verdikolonnen, som `minorsPresent`: den ER handlingen.
                Tilstanden bæres av knottens posisjon og hakemerket (DD-08) — setningen ved
                siden av sier hva tilstanden BETYR, aldri «På»/«Av» (F8). Med kravet av er
                setningen et varsel med ikon OG tekst, fordi farge alene aldri bærer mening. */}
            <Rad
              id="adminMfaRequired"
              navn={BEKREFTELSE.navn}
              kode="adminMfaRequired"
              handling={null}
              endret={endret('adminMfaRequired')}
              verdi={
                <span className="panel-innst-bryterverdi">
                  <Bryter
                    label={BEKREFTELSE.navn}
                    skjultEtikett
                    paa={mfa}
                    onChange={(v) => (v ? sett('adminMfaRequired', true) : setBekreftAv(true))}
                    testid="mfa"
                  />
                  {mfa ? (
                    <span className="panel-innst-status">
                      <Ikon navn="shield-checkmark-outline" /> {BEKREFTELSE.paa}
                    </span>
                  ) : (
                    <span className="panel-innst-status panel-innst-uverifisert">
                      <Ikon navn="warning-outline" /> {BEKREFTELSE.av}
                    </span>
                  )}
                </span>
              }
            />
            <div className="panel-innst-om">
              <Om tittel="tofaktor ved innlogging" testid="om-tofaktor">
                <p>Slått på bekrefter hver panelbruker innloggingen en gang til — med koden fra autentiseringsappen, eller med enheten sin. En ny organisasjon starter med det av; eieren slår det på her.</p>
                <p>Står det av, kommer panelbrukerne inn med e-postkoden alene. Sakene brukerne deres sender inn er personopplysninger, og de står da bak ett ledd i stedet for to. Valget er deres, og risikoen ved det lavere nivået er deres.</p>
                <p>Endringen skrives i loggen med hvem som gjorde den og når. Det vi holder uansett: økten varer aldri mer enn åtte timer fra første innlogging, og det finnes ingen «husk denne nettleseren».</p>
              </Om>
            </div>

            {harHypoteser ? (
              <>
                {/* H2.7 (DD-78): domenene og hva de gir i verdilinjen, aldri bare tallet. Panelet
                    gjør små bokstaver og fjerner mellomrom; resten avgjør tjeneren (409, 400, 422)
                    og teksten står ved knappen (DD-58). */}
                <Rad
                  id="domeneseter"
                  navn="Domeneseter"
                  kode="seatDomains"
                  handling="Endre"
                  endret={endret('domeneseter')}
                  verdi={domeneseter.length ? <><strong>{domeneseter.join(', ')}</strong> · alle med adresse her får sete som bidragsyter i Hypoteser</> : 'Ingen'}
                  verdiSmal={domeneseter.length ? `${domeneseter.length} domene${domeneseter.length === 1 ? '' : 'r'}` : 'Ingen'}
                >
                  <label className="panel-felt panel-innst-bred">
                    <span>Domener (kommaseparert)</span>
                    <input type="text" value={domeneseter.join(', ')} placeholder="example.no" onChange={(e) => sett('seatDomains', e.target.value.toLowerCase().split(',').map((x) => x.trim()).filter(Boolean))} data-testid="domeneseter" />
                  </label>
                  <p className="panel-tekst-liten">Alle med en bekreftet adresse på et av domenene får sete som medlem med bidragsyter i Hypoteser første gang de logger inn. Ingen andre rom, ingen annen rolle — det gir du i Panelbrukere.</p>
                </Rad>
                <div className="panel-innst-om">
                  <Om tittel="domeneseter" testid="om-domeneseter">
                    <p>Alle med postkasse på domenet kommer inn, også innleide og fellespostkasser. Setet dukker opp i Panelbrukere som «Venter på første innlogging», og du kan deaktivere det der som alle andre.</p>
                    <p>Et domene kan bare høre til én organisasjon på plattformen, og offentlige leverandører som gmail.com kan ikke brukes. Fjerner du et domene, beholder de som alt har sete, setet sitt.</p>
                  </Om>
                </div>
              </>
            ) : null}
            <PanelbrukereRad />
            <ApiNoekler onMelding={(tekst) => setToast({ tekst })} />
            <div className="panel-innst-om">
              {/* DD-20: løftet står ÉN gang per side. */}
              <Om tittel="nøklene" testid="om-noeklene">
                <p>API-nøkkelen er hemmelig og gir serveren deres tilgang med akkurat de tillatelsene den får — ikke mer.</p>
                <p>En nøkkel vises én gang, når den lages. Etterpå finnes bare de første tegnene — det finnes ingen «vis»-knapp noe sted i panelet.</p>
              </Om>
            </div>
          </Seksjon>

          <Webhooks erEier kanTeste onMelding={(tekst) => setToast({ tekst })} />
        </RadProvider>
      </div>

      <Fot antall={endringer} endretNavn={endretNavn} lagrer={oppdater.isPending} feil={feil} onLagre={lagre} onForkast={tøm} onLukkFeil={() => setFeil(null)} />

      {bekreftAv ? (
        <Dialog tittelId="mfa-h" onLukk={() => setBekreftAv(false)} testid="mfa-dialog">
          <h2 id="mfa-h" className="panel-h2">
            Slå av tofaktor ved innlogging?
          </h2>
          <p className="panel-tekst">Panelbrukerne kommer da inn med e-postkoden alene — ingen kode fra appen, ingen enhet. Alt brukerne deres har sendt inn står bak ett ledd i stedet for to. Valget er deres, og risikoen ved det lavere nivået er deres; endringen skrives i loggen med hvem og når.</p>
          {/* Utkastet, ikke serveren: derfor er knappen ikke rød (DD-53, vakt 10). Setningen
              sier hvor endringen står nå, så «Slå av» ikke leses som at det er gjort. */}
          <p className="panel-tekst">Det skjer når du lagrer siden. Til da kan du forkaste endringen i foten.</p>
          <div className="panel-handlinger">
            {/* Primær, ikke rød: dialogens avsluttende handling skal se ut som en handling
                (vakt 7), men rødt er forbeholdt det som fjernes på serveren (DD-53). */}
            <Knapp
              label="Slå av"
              primaer
              onClick={() => {
                sett('adminMfaRequired', false);
                setBekreftAv(false);
              }}
              testid="mfa-bekreft"
            />
            <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={() => setBekreftAv(false)} />
          </div>
        </Dialog>
      ) : null}

      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}
